import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { v4 as uuid } from 'uuid';
import { enhanceGLBridge, fixZipResUrl, IEnhanceParams } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { getFramework, IFramework } from '@/framework';
import { type MainLayer } from '@/layers/main-layer';
import { applyEnhanceGLResult } from '@/layers/main-layer/actions/enhance';
import { checkNeedMergeCanvas } from '@/layers/main-layer/utils';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { createFromCanvas } from '@/utils/layer-kit';
import { IImageLayerProps, ILayerProps, LayerType } from '@/version-types';
import { defaultEnhanceParams } from '../config';
import { getImageEnhanceState, IStore, watchImageEnhanceChange } from '../states/image-enhance-state';
import { adapterEnhanceArgs, getHslArgs, getPosterizeArgs } from '../utils';

const assets = {
    id: 'Enhance',
    zip: assetsConfig.filter_Enhance,
};

export class BasicImageEnhance {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    // 功能使用记录
    private enhanceUsedList = new Set<string>();
    private customEvents = '';

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getImageEnhanceState(identity);

        watchImageEnhanceChange(identity, (state) => {
            this.store = state;
        });
    }

    getCustomEvents() {
        return this.customEvents;
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.getInstance<MainLayer>('MainLayer');
        if (!mainLayer) {
            throw new Error('MainLayer 实例不存在');
        }
        return mainLayer;
    }

    async create(
        _originLayer: ILayerProps,
        customEvents?: string,
        isBatchEditor?: boolean,
    ): Promise<HTMLCanvasElement | null> {
        const originLayer = await this.tryGetMainLayer().enterMergeMode(_originLayer);
        const newLayer = cloneDeep(originLayer);

        this.store.setOriginLayer(originLayer);
        const url = (newLayer.data as IImageLayerProps).url;
        // 初始化gl数据
        const fromCanvas = await createFromCanvas(newLayer.id, url);
        const glCanvas = await enhanceGLBridge.from(fromCanvas).create(assets.id, fixZipResUrl(assets.zip));

        newLayer.__useNavigatorRender = isBatchEditor;
        if (isBatchEditor) {
            newLayer.__isDoEnhance = true;
            this.framework.stage.addOrUpdate(newLayer);
            setTimeout(() => {
                newLayer.__isDoEnhance = true;
                newLayer.__renderEnhanceCanvas = true;
                this.framework.stage.addOrUpdate(newLayer);
            }, 10);
        } else {
            newLayer.__renderEnhanceCanvas = true;
            this.framework.stage.addOrUpdate(newLayer);
        }

        this.customEvents = customEvents ?? '';

        return glCanvas;
    }

    private async destroy() {
        const layerId = this.store.originLayer?.id || '';
        const layer = cloneDeep(this.framework.getLayer(layerId));

        // HACK: 防御性代码，防止 originLayer 为空
        if (!layer) {
            return;
        }

        delete layer.__isDoEnhance;
        delete layer.__renderEnhanceCanvas;
        delete layer.__useNavigatorRender;
        this.framework.stage.addOrUpdate(layer);
        this.store.resetEnhance();
        this.enhanceUsedList.clear();
        this.tryGetMainLayer().exitMergeMode();
    }

    async batchOneAction(params: IEnhanceParams): Promise<void> {
        const { framework } = this;
        const newLayer = cloneDeep(framework.getLayer(this.store.originLayer?.id + ''));

        // HACK: 防御性代码，防止 originLayer 为空
        if (!newLayer) {
            return;
        }

        newLayer.__isDoEnhance = true;
        this.framework.stage.addOrUpdate(newLayer);
        const args = {};

        const baseData = this.store.baseEnhance;
        const colorData = this.store.colorEnhance;
        const lightData = this.store.lightEnhance;
        const particularsData = this.store.particularsEnhance;
        const posterizeData = this.store.posterizeEnhance;
        const hslData = this.store.hslEnhance;

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            params,
        );

        await enhanceGLBridge.setParams(adapterEnhanceArgs(args));
    }

    /**
     * 实时应用效果，但不修改图层结果和保存到历史记录
     *
     * @param params - 参数
     * @param trackFunc - 方法
     */
    async action(params: IEnhanceParams, trackFunc?: string): Promise<void> {
        const { framework } = this;
        const newLayer = cloneDeep(framework.getLayer(this.store.originLayer?.id + ''));

        // HACK: 防御性代码，防止 originLayer 为空
        if (!newLayer) {
            return;
        }

        // ATTENTION: 当前更新图层数据，仅为了触发图层进入 GL 渲染流程
        newLayer.__isDoEnhance = true;
        framework.stage.addOrUpdate(newLayer);
        if (trackFunc) this.enhanceUsedList.add(trackFunc);

        const args = {};

        const baseData = this.store.baseEnhance;
        const colorData = this.store.colorEnhance;
        const lightData = this.store.lightEnhance;
        const particularsData = this.store.particularsEnhance;
        const posterizeData = this.store.posterizeEnhance;
        const hslData = this.store.hslEnhance;

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            params,
        );

        await enhanceGLBridge.setParams(adapterEnhanceArgs(args));
    }

    async batchReset(): Promise<void> {
        for (let i = 0; i < this.store.originLayers.length; i++) {
            const mainLayer = this.store.originLayers[i];
            if (!mainLayer) {
                continue;
            }

            this.framework.stage.addOrUpdate(cloneDeep(mainLayer));
        }

        await new Promise((resolve) => {
            setTimeout(async () => {
                const originLayer = this.store.originLayer;
                if (originLayer) {
                    await this.create(originLayer, undefined, true);
                    await this.action({});
                }
                resolve(true);
            }, 10);
        });
    }

    async batchAction(
        params: IEnhanceParams,
        onStageLoading?: (stageIndex: number, isBatch: boolean) => void,
    ): Promise<void> {
        console.time('basic enhance batch action');
        const { framework } = this;
        const args = {};

        const baseData = this.store.baseEnhance;
        const colorData = this.store.colorEnhance;
        const lightData = this.store.lightEnhance;
        const particularsData = this.store.particularsEnhance;
        const posterizeData = this.store.posterizeEnhance;
        const hslData = this.store.hslEnhance;

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            params,
        );

        for (let i = 1; i < this.store.originLayers.length; i++) {
            try {
                const mainLayer = cloneDeep(this.store.originLayers[i]);

                if (!mainLayer) {
                    continue;
                }

                const { naturalHeight, naturalWidth, imageTransform } = mainLayer.data as IImageLayerProps;
                const { img } = await getImgInfoAsync((mainLayer.data as IImageLayerProps).url);
                const { canvas } = createCanvas(
                    naturalWidth * imageTransform.scale,
                    naturalHeight * imageTransform.scale,
                    img,
                );
                await enhanceGLBridge.from(canvas).create(assets.id, fixZipResUrl(assets.zip));
                await enhanceGLBridge.setParams(adapterEnhanceArgs(args));
                const { canvas: resultCanvas } = await enhanceGLBridge.getGLResult();

                if (resultCanvas) {
                    const base64 = resultCanvas.toDataURL();
                    const data = mainLayer.data as IImageLayerProps;

                    data.url = base64Kit.getCode(base64, uuid());
                }

                framework.stage.addOrUpdate(cloneDeep(mainLayer));
                onStageLoading?.(i, false);
            } catch (error) {
                console.error(error);
                onStageLoading?.(i, false);
            }
        }
        console.timeEnd('basic enhance batch action');
    }

    async getResult(layer: ILayerProps) {
        await applyEnhanceGLResult(layer);
        this.framework.stage.addOrUpdate(cloneDeep(layer));
    }

    /**
     * 应用效果，并保存到历史记录
     */
    async applyConfirm() {
        const { framework, enhanceUsedList } = this;
        const oldLayer = this.store.originLayer;

        if (!oldLayer) {
            return;
        }

        const newLayer = cloneDeep(framework.getLayer(oldLayer.id));

        // HACK: 防御性代码，防止 getLayer 为空
        if (!newLayer) {
            this.store.resetEnhance();
            return;
        }

        const isDoEnhance = newLayer.__isDoEnhance;

        if (!isDoEnhance) {
            this.destroy();
            return;
        }

        const currentParams = {
            baseEnhance: this.store.baseEnhance,
            colorEnhance: this.store.colorEnhance,
            lightEnhance: this.store.lightEnhance,
            particularsEnhance: this.store.particularsEnhance,
            posterizeEnhance: this.store.posterizeEnhance,
            hslEnhance: this.store.hslEnhance,
        };

        // 参数没有变化，则不写入历史记录
        if (JSON.stringify(currentParams) === JSON.stringify(defaultEnhanceParams)) {
            this.destroy();
            return;
        }

        await applyEnhanceGLResult(newLayer);
        framework.stage.addOrUpdate(newLayer);

        const trackFunc = [...enhanceUsedList].join(',');
        const mergeResult = this.tryGetMainLayer().applyMergeMode(newLayer, trackFunc);

        if (!mergeResult) {
            framework.history.write(oldLayer, newLayer, trackFunc);
        }

        this.store.resetEnhance();
        this.store.setOriginLayer(null);
        this.enhanceUsedList.clear();
        // 有应用效果则返回 true
        return true;
    }

    async batchApplyConfirm() {
        const currentParams = {
            baseEnhance: this.store.baseEnhance,
            colorEnhance: this.store.colorEnhance,
            lightEnhance: this.store.lightEnhance,
            particularsEnhance: this.store.particularsEnhance,
            posterizeEnhance: this.store.posterizeEnhance,
            hslEnhance: this.store.hslEnhance,
        };

        // 参数没有变化，则不写入历史记录
        if (JSON.stringify(currentParams) === JSON.stringify(defaultEnhanceParams)) {
            this.destroy();
            return;
        }

        const trackFunc = [...this.enhanceUsedList].join(',');
        const currentMainLayers = [] as ILayerProps[];
        const stageList = this.framework.getStages();
        for (let i = 0; i < stageList.length; i++) {
            const mainLayer = stageList[i].find((layer) => layer.layerType === LayerType.MAIN_LAYER);
            if (!mainLayer) {
                continue;
            }
            if (i === 0) {
                const { naturalHeight, naturalWidth, imageTransform } = mainLayer.data as IImageLayerProps;
                const { img } = await getImgInfoAsync((mainLayer.data as IImageLayerProps).url);
                const { canvas } = createCanvas(
                    naturalWidth * imageTransform.scale,
                    naturalHeight * imageTransform.scale,
                    img,
                );
                await enhanceGLBridge.from(canvas).create(assets.id, fixZipResUrl(assets.zip));
                const args = {};

                const baseData = this.store.baseEnhance;
                const colorData = this.store.colorEnhance;
                const lightData = this.store.lightEnhance;
                const particularsData = this.store.particularsEnhance;
                const posterizeData = this.store.posterizeEnhance;
                const hslData = this.store.hslEnhance;

                merge(
                    args,
                    baseData,
                    lightData,
                    colorData,
                    particularsData,
                    getPosterizeArgs(posterizeData),
                    getHslArgs(hslData),
                );
                await enhanceGLBridge.setParams(adapterEnhanceArgs(args));
                const { canvas: resultCanvas } = await enhanceGLBridge.getGLResult();

                if (resultCanvas) {
                    const base64 = resultCanvas.toDataURL();
                    const data = mainLayer.data as IImageLayerProps;

                    data.url = base64Kit.getCode(base64, uuid());
                }
                mainLayer.__isDoEnhance = false;
                mainLayer.__renderEnhanceCanvas = false;
                mainLayer.__useNavigatorRender = false;

                this.framework.stage.addOrUpdate(cloneDeep(mainLayer));
                await this.getResult(mainLayer);
            }
            currentMainLayers.push(mainLayer);
        }
        this.framework.history.write(cloneDeep(this.store.originLayers), cloneDeep(currentMainLayers), trackFunc);
        this.store.resetEnhance();
        this.store.setOriginLayers(cloneDeep(currentMainLayers));
        this.store.setOriginLayer(null);
        this.enhanceUsedList.clear();
        // 有应用效果则返回 true
        return true;
    }
}
